// =============================================================================
// features/admin/inbox-assignments/hooks.ts — TanStack Query hooks (F47-S08).
//
// Hooks:
//   - useInboxAssignments()     — lista as atribuições da organização (query)
//   - useCreateInboxAssignment() — atribuir uma inbox a um usuário (mutation)
//   - useDeleteInboxAssignment() — remover uma atribuição (mutation)
//
// Nunca useEffect + fetch — apenas TanStack Query. Invalida a lista após
// mutate para manter o cache consistente.
// =============================================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../lib/api';

import { createInboxAssignment, deleteInboxAssignment, fetchInboxAssignments } from './api';
import type { ChannelAssignmentResponse, CreateAssignmentBody } from './api';

export const INBOX_ASSIGNMENTS_QUERY_KEY = ['livechat', 'inbox-assignments'] as const;

// ---------------------------------------------------------------------------
// useInboxAssignments
// ---------------------------------------------------------------------------

export function useInboxAssignments(): {
  data: ChannelAssignmentResponse[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: INBOX_ASSIGNMENTS_QUERY_KEY,
    queryFn: fetchInboxAssignments,
    staleTime: 15_000,
  });

  return { data, isLoading, isError, refetch };
}

// ---------------------------------------------------------------------------
// useCreateInboxAssignment
// ---------------------------------------------------------------------------

export function useCreateInboxAssignment(): {
  mutate: (body: CreateAssignmentBody) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (body: CreateAssignmentBody) => createInboxAssignment(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INBOX_ASSIGNMENTS_QUERY_KEY });
      toast('Inbox atribuída com sucesso.', 'success');
    },
    onError: (error: unknown) => {
      const message = error instanceof ApiError ? error.message : 'Falha ao atribuir a inbox.';
      toast(message, 'danger');
    },
  });

  return { mutate: mutation.mutate, isPending: mutation.isPending };
}

// ---------------------------------------------------------------------------
// useDeleteInboxAssignment
// ---------------------------------------------------------------------------

export function useDeleteInboxAssignment(): {
  mutate: (id: string) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (id: string) => deleteInboxAssignment(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INBOX_ASSIGNMENTS_QUERY_KEY });
      toast('Atribuição removida.', 'success');
    },
    onError: (error: unknown) => {
      const message = error instanceof ApiError ? error.message : 'Falha ao remover a atribuição.';
      toast(message, 'danger');
    },
  });

  return { mutate: mutation.mutate, isPending: mutation.isPending };
}
